import React from 'react';
import { createPortal } from 'react-dom';
import useMediaQuery from '../../utils/useMediaQuery';

/**
 * The shared shell for in-app notices.
 * docs/plans/2026-08-handoff-layout/09-app-notices.md
 *
 * Desktop: a 560px centred modal over a scrim. Mobile: a bottom sheet with a grab bar.
 * Headlines are sentence case — never uppercased by inherited h2 rules.
 *
 * Two tones, because the two notices answer to different handoffs. `light` is the brutalist
 * white card an admin announcement is composed against (its accent bands assume it).
 * `dark` is the Portal surface — #111, radius 28, the #1F1F1F ring — which the resume notice
 * and the profile photo gate wear (design_handoff_onboarding_v2, "Resume Notice v2"). `ink` is
 * the admin announcement's sheet (design_handoff_app_notices "ManualNotice"): #0A0A0A, a 40×4
 * grabber, 440 wide on desktop with every corner at 28.
 */
export default function AppNoticeShell({ children, onClose, tone = 'light' }) {
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const ink = tone === 'ink';
  const dark = tone === 'dark' || ink;

  return createPortal(
    <div
      className={`pr-overlay-root fixed inset-0 z-[95] flex ${dark ? 'pk' : ''} ${isDesktop ? 'items-center justify-center p-6' : 'items-end'}`}
      style={{ background: dark ? 'rgba(0,0,0,.72)' : 'rgba(0,0,0,.45)' }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={
          dark
            ? `w-full ${ink ? 'max-w-[440px]' : 'max-w-[560px]'} overflow-y-auto text-white ${isDesktop ? 'max-h-[calc(100vh-48px)] rounded-[28px]' : 'max-h-[92vh] rounded-t-[28px] safe-bottom-inset'}`
            : `w-full bg-white ${isDesktop ? 'max-w-[560px] border-2 border-black shadow-hardLg' : 'border-t-2 border-black safe-bottom-inset'}`
        }
        style={ink ? { background: '#0A0A0A', boxShadow: '0 24px 60px rgba(0,0,0,.6)' } : dark ? { background: '#111', boxShadow: 'inset 0 0 0 1px #1F1F1F, 0 24px 60px rgba(0,0,0,.6)' } : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        {!isDesktop && (
          <div className={`flex justify-center ${ink ? 'pt-3' : 'py-2.5'}`}>
            <span className={ink ? 'block h-1 w-10 rounded-full' : dark ? 'block h-[5px] w-11 rounded-full' : 'block h-[5px] w-16 bg-black'} style={ink ? { background: '#2E2E2E' } : dark ? { background: '#333' } : undefined} aria-hidden />
          </div>
        )}
        {children}
      </div>
    </div>,
    document.body,
  );
}
